import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { clearProfileCache, clearDashboardAdsCache } from '../../slices/ads/reducer';
import { clearAdsCache } from '../../slices/dashboard/reducer';

const CACHE_INVALIDATION_EVENTS = [
    'profile-created',
    'profile-updated', 
    'profile-deleted',
    'ad-created',
    'ad-updated',
    'ad-deleted',
    'payment-completed',
    'profile-status-changed'
];

export const useCacheInvalidation = () => {
    const dispatch = useDispatch();

    // Clear all caches
    const clearAllCaches = useCallback(() => {
        dispatch(clearProfileCache());
        dispatch(clearDashboardAdsCache());
        dispatch(clearAdsCache());
    }, [dispatch]);

    // Clear profile cache only
    const clearProfileCacheOnly = useCallback(() => {
        dispatch(clearProfileCache());
    }, [dispatch]);

    // Clear dashboard ads cache only
    const clearDashboardCacheOnly = useCallback(() => {
        dispatch(clearDashboardAdsCache());
        dispatch(clearAdsCache());
    }, [dispatch]);

    // Invalidate cache based on event type
    const invalidateCache = useCallback((eventType) => {
        switch (eventType) {
            case 'profile-created':
            case 'profile-updated':
            case 'profile-deleted':
            case 'profile-status-changed':
                clearProfileCacheOnly();
                break;
            case 'ad-created':
            case 'ad-updated':
            case 'ad-deleted':
            case 'payment-completed':
                clearAllCaches();
                break;
            default:
                clearAllCaches();
        }
    }, [clearProfileCacheOnly, clearAllCaches]);

    // Set up event listeners for cache invalidation
    const setupCacheInvalidation = useCallback(() => {
        const handleStorageChange = (e) => {
            if (CACHE_INVALIDATION_EVENTS.includes(e.key)) {
                invalidateCache(e.key);
            }
        };

        const handleCustomEvent = (e) => {
            if (CACHE_INVALIDATION_EVENTS.includes(e.detail?.eventType)) {
                invalidateCache(e.detail.eventType);
            }
        };

        // Listen to localStorage changes (for cross-tab communication)
        window.addEventListener('storage', handleStorageChange);
        
        // Listen to custom events
        window.addEventListener('cache-invalidation', handleCustomEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('cache-invalidation', handleCustomEvent);
        };
    }, [invalidateCache]);

    // Trigger cache invalidation event
    const triggerCacheInvalidation = useCallback((eventType) => {
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('cache-invalidation', {
            detail: { eventType }
        }));

        // Also update localStorage for cross-tab communication
        localStorage.setItem(eventType, Date.now().toString());
        localStorage.removeItem(eventType);
    }, []);

    return {
        clearAllCaches,
        clearProfileCacheOnly,
        clearDashboardCacheOnly,
        invalidateCache,
        setupCacheInvalidation,
        triggerCacheInvalidation
    };
};

export default useCacheInvalidation;
